import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Grid,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import axios from 'axios';
import { CSVLink } from 'react-csv';

const csvHeaders = [
  { label: 'Invoice No.', key: 'inv_no' },
  { label: 'Username', key: 'username' },
  { label: 'Item Name', key: 'item_name' },
  { label: 'Unit Price', key: 'unitprice' },
  { label: 'Quantity', key: 'qty' },
  { label: 'Line Total', key: 'total' },
  { label: 'Bill Date/Time', key: 'creation_date' },
  { label: 'Status', key: 'statusLabel' },
];

const formatCurrency = (value) => {
  const numeric = Number(value);
  if (Number.isNaN(numeric)) {
    return value ?? '-';
  }
  return numeric.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const formatLocalDate = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const getMonthBounds = () => {
  const today = new Date();
  const start = new Date(today.getFullYear(), today.getMonth(), 1);
  return {
    start: formatLocalDate(start),
    end: formatLocalDate(today),
  };
};

const AllInvoiceReport = () => {
  const monthBounds = useMemo(() => getMonthBounds(), []);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [invoiceQuery, setInvoiceQuery] = useState('');
  const [fromDate, setFromDate] = useState(monthBounds.start);
  const [toDate, setToDate] = useState(monthBounds.end);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  useEffect(() => {
    const fetchInvoices = async () => {
      setLoading(true);
      setError('');
      try {
        const response = await axios.get('https://servergj.ccsoftwares.com/sales/getAllBills');
        const enriched = (response.data || []).map((invoice) => {
          const createdAt = invoice.creation_date ? new Date(invoice.creation_date) : null;
          return {
            ...invoice,
            creationDateObj: createdAt,
            creationDateLabel: createdAt ? createdAt.toLocaleString() : 'N/A',
            statusLabel: invoice.status === 1 ? 'Active' : 'Deleted',
          };
        });
        enriched.sort((a, b) => {
          if (a.creationDateObj && b.creationDateObj) {
            return b.creationDateObj - a.creationDateObj;
          }
          if (a.creationDateObj) return -1;
          if (b.creationDateObj) return 1;
          return 0;
        });
        setInvoices(enriched);
      } catch (err) {
        console.error('Failed to fetch invoices', err);
        setError('Unable to load invoices at the moment.');
      } finally {
        setLoading(false);
      }
    };

    fetchInvoices();
  }, []);

  useEffect(() => {
    setPage(0);
  }, [invoiceQuery, fromDate, toDate]);

  const filteredInvoices = useMemo(() => {
    if (!invoices.length) return [];

    const normalizedQuery = invoiceQuery.trim().toLowerCase();
    const from = fromDate ? new Date(fromDate) : null;
    if (from) {
      from.setHours(0, 0, 0, 0);
    }
    const to = toDate ? new Date(toDate) : null;
    if (to) {
      to.setHours(23, 59, 59, 999);
    }

    return invoices.filter((invoice) => {
      const matchesInvoice = normalizedQuery
        ? invoice.inv_no?.toLowerCase().includes(normalizedQuery) ||
          invoice.item_name?.toLowerCase().includes(normalizedQuery)
        : true;

      if (!matchesInvoice) {
        return false;
      }

      if (!from && !to) {
        return true;
      }

      if (!invoice.creationDateObj) {
        return false;
      }

      const afterFrom = from ? invoice.creationDateObj >= from : true;
      const beforeTo = to ? invoice.creationDateObj <= to : true;
      return afterFrom && beforeTo;
    });
  }, [invoices, invoiceQuery, fromDate, toDate]);

  const todaysNetSales = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return filteredInvoices.reduce((accumulator, invoice) => {
      if (!invoice.creationDateObj) return accumulator;
      const invoiceDate = new Date(invoice.creationDateObj);
      invoiceDate.setHours(0, 0, 0, 0);
      if (invoiceDate.getTime() === today.getTime()) {
        return accumulator + Number(invoice.total || 0);
      }
      return accumulator;
    }, 0);
  }, [filteredInvoices]);

  const selectedPeriodNetSales = useMemo(
    () =>
      filteredInvoices.reduce(
        (accumulator, invoice) => accumulator + Number(invoice.total || 0),
        0
      ),
    [filteredInvoices]
  );

  const selectedPeriodQuantity = useMemo(
    () =>
      filteredInvoices.reduce(
        (accumulator, invoice) => accumulator + Number(invoice.qty || 0),
        0
      ),
    [filteredInvoices]
  );

  const paginatedInvoices = useMemo(() => {
    if (rowsPerPage === -1) {
      return filteredInvoices;
    }
    const startIndex = page * rowsPerPage;
    return filteredInvoices.slice(startIndex, startIndex + rowsPerPage);
  }, [filteredInvoices, page, rowsPerPage]);

  const csvData = useMemo(
    () =>
      filteredInvoices.map((invoice) => ({
        ...invoice,
        creation_date: invoice.creationDateLabel,
      })),
    [filteredInvoices]
  );

  const handleChangePage = (_event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  return (
    <Box sx={{ p: 2 }}>
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        spacing={2}
        justifyContent="space-between"
        alignItems={{ xs: 'flex-start', md: 'center' }}
        sx={{ mb: 3 }}
      >
        <Box>
          <Typography variant="h5" fontWeight={700}>
            All Invoices Report
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Track invoiced line items with flexible search and date filters.
          </Typography>
        </Box>
        <CSVLink
          data={csvData}
          headers={csvHeaders}
          filename="all-invoices-report.csv"
          style={{ textDecoration: 'none' }}
        >
          <Button
            variant="contained"
            startIcon={<DownloadIcon />}
            sx={{ textTransform: 'none', borderRadius: 2 }}
            disabled={!filteredInvoices.length}
          >
            Download CSV
          </Button>
        </CSVLink>
      </Stack>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 2.5, borderRadius: 2, bgcolor: '#F1F5F9' }}>
            <Typography variant="body2" color="text.secondary">
              Today&apos;s Net Sales
            </Typography>
            <Typography variant="h5" fontWeight={700} sx={{ mt: 0.5 }}>
              Rs. {formatCurrency(todaysNetSales)}
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 2.5, borderRadius: 2, bgcolor: '#F1F5F9' }}>
            <Typography variant="body2" color="text.secondary">
              Selected Period Net Sales
            </Typography>
            <Typography variant="h5" fontWeight={700} sx={{ mt: 0.5 }}>
              Rs. {formatCurrency(selectedPeriodNetSales)}
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 2.5, borderRadius: 2, bgcolor: '#F1F5F9' }}>
            <Typography variant="body2" color="text.secondary">
              Selected Period Quantity
            </Typography>
            <Typography variant="h5" fontWeight={700} sx={{ mt: 0.5 }}>
              {selectedPeriodQuantity.toLocaleString()}
            </Typography>
          </Paper>
        </Grid>
      </Grid>

      <Paper sx={{ p: 2, mb: 3, borderRadius: 2 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <TextField
              label="Search by Invoice or Item"
              placeholder="Invoice No or Item Name"
              fullWidth
              value={invoiceQuery}
              onChange={(event) => setInvoiceQuery(event.target.value)}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              type="date"
              label="From Date"
              InputLabelProps={{ shrink: true }}
              fullWidth
              value={fromDate}
              onChange={(event) => setFromDate(event.target.value)}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              type="date"
              label="To Date"
              InputLabelProps={{ shrink: true }}
              fullWidth
              value={toDate}
              onChange={(event) => setToDate(event.target.value)}
            />
          </Grid>
        </Grid>
      </Paper>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Typography color="error" sx={{ mt: 2 }}>
          {error}
        </Typography>
      ) : (
        <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
          <Table>
            <TableHead sx={{ backgroundColor: '#000000' }}>
              <TableRow>
                <TableCell sx={{ color: 'white', fontWeight: 600 }}>Invoice No</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 600 }}>Username</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 600 }}>Item Name</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 600 }}>Qty</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 600 }}>Unit Price (Rs.)</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 600 }}>Line Total (Rs.)</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 600 }}>Bill Date / Time</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 600 }}>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedInvoices.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    <Typography variant="body2" color="text.secondary">
                      No invoice records match your filters.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                paginatedInvoices.map((invoice, index) => (
                  <TableRow key={`${invoice.inv_no}-${invoice.invdetails_key || index}`}>
                    <TableCell>{invoice.inv_no}</TableCell>
                    <TableCell>{invoice.username}</TableCell>
                    <TableCell>{invoice.item_name}</TableCell>
                    <TableCell>{invoice.qty}</TableCell>
                    <TableCell>{formatCurrency(invoice.unitprice)}</TableCell>
                    <TableCell>{formatCurrency(invoice.total)}</TableCell>
                    <TableCell>{invoice.creationDateLabel}</TableCell>
                    <TableCell>{invoice.statusLabel}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {!loading && !error && (
        <TablePagination
          component="div"
          count={filteredInvoices.length}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[10, 25, 50, 100, { label: 'All', value: -1 }]}
          sx={{ mt: 2 }}
        />
      )}
    </Box>
  );
};

export default AllInvoiceReport;
